import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, Firestore } from "firebase/firestore";
import { getStorage, connectStorageEmulator, FirebaseStorage } from "firebase/storage";
import { pickAuthDomain } from "./auth-domain";

// Firebase Console → Authentication → Settings → 승인된 도메인
// 반드시 포함되어야 할 도메인:
// - map-experts.vercel.app (프로덕션)
// - localhost (개발)
//
// authDomain (2026-10-22 대표 지시 · 구글 로그인 창에 "trinitymap2026.firebaseapp.com" 이 보이지 않게):
//   운영 주소 (www.maptest.kr) 에서는 사이트 자기 주소를 authDomain 으로 쓴다 → 로그인 창 주소·구글 "○○(으)로 이동" 이 www.maptest.kr.
//   /__/auth/* 는 next.config.ts rewrites 가 Firebase 기본 도메인으로 그대로 넘긴다 (Firebase 권장 방식 "reverse proxy").
//   구글 OAuth 클라이언트에 https://www.maptest.kr/__/auth/handler 가 등록돼 있음을 2026-10-22 확인 (미등록 주소는 oauth/error · 등록 주소는 로그인 화면).
//   그 밖의 주소 (localhost · 미리보기 배포) 는 기존 env 값 그대로.
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: pickAuthDomain(
    typeof window !== "undefined" ? window.location.hostname : null,
    typeof window !== "undefined" ? window.location.host : null,
    process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  ),
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

export const auth = getAuth(app);

// 로컬 E2E 전용 : NEXT_PUBLIC_USE_FIREBASE_EMULATOR=1 로 빌드했을 때만 에뮬레이터에 붙는다.
// Vercel 에는 이 변수가 없다 → 빌드 시 인라인되지 않고 런타임 값 undefined → 항상 false (분기 미실행).
const USE_EMULATOR = process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATOR === "1";
if (USE_EMULATOR) connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });

// Explicit persistence so auth state survives the Toss redirect roundtrip on
// in-app browsers (KakaoTalk, NAVER) where the default selection can fall
// through to in-memory and the post-payment success page lands with no
// currentUser — producing a confirm() call with no Authorization header.
// Prefer IndexedDB (more durable on modern webviews); fall back to localStorage
// when IndexedDB is unavailable or blocked by the embedded browser.
if (typeof window !== "undefined") {
  setPersistence(auth, indexedDBLocalPersistence)
    .catch(() => setPersistence(auth, browserLocalPersistence))
    .catch((err) => console.error("[firebase] persistence setup failed", err));
}

// Dev 또는 에뮬레이터 빌드 전용 : E2E capture 를 위해 signInWithCustomToken 을 window 에 노출한다.
// process.env.NODE_ENV 는 프로덕션 빌드에서 상수로 치환돼 이 블록이 제거된다.
if (typeof window !== "undefined" && (process.env.NODE_ENV === "development" || USE_EMULATOR)) {
  import("firebase/auth").then((mod) => {
    (window as unknown as { __signInWithCustomToken?: (t: string) => Promise<unknown> }).__signInWithCustomToken =
      (t) => mod.signInWithCustomToken(auth, t);
  }).catch(() => {});
}

let db: Firestore;
try {
  db = getFirestore(app);
  if (USE_EMULATOR) connectFirestoreEmulator(db, "127.0.0.1", 8080);
} catch (e) {
  console.warn("Firestore initialization failed:", e);
  db = null as unknown as Firestore;
}

let storage: FirebaseStorage;
try {
  storage = getStorage(app);
  if (USE_EMULATOR) connectStorageEmulator(storage, "127.0.0.1", 9199);
} catch (e) {
  console.warn("Firebase Storage initialization failed:", e);
  storage = null as unknown as FirebaseStorage;
}

export { db, storage };
export default app;
